import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  RobotOutlined, 
  CustomerServiceOutlined, 
  BulbOutlined, 
  MessageOutlined,
  SoundOutlined,
  MutedOutlined
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { sanitizeInput, sanitizeContent, RateLimiter } from './chatSecurity';


const parseMarkdown = (text) => {
  if (!text) return '';
  
  let parsed = text
      // Handle code blocks first
      .replace(/```([\s\S]*?)```/g, (_, code) => `<pre><code>${code.trim()}</code></pre>`)
      
      // Handle inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      
      // Handle bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/__([^_]*?)__/g, '<strong>$1</strong>')
      
      // Handle italic
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/_([^_]*?)_/g, '<em>$1</em>')
      
      // Handle images - ADD THIS NEW SECTION
      .replace(/!\[(.*?)\]\((.*?)\)/g, (match, alt, src) => {
          // Check if URL is allowed (you can expand this check)
          if (isAllowedImageUrl(src)) {
              return `<img src="${src}" alt="${alt}" class="chat-image" />`;
          }
          // Fallback to a link if image URL is not allowed
          return `<a href="${src}" target="_blank" rel="noopener noreferrer">${alt || src}</a>`;
      })
      
      // Handle unordered lists
      .replace(/^[\s]*?[-*+][\s]+(.*?)(?=(?:^[\s]*?[-*+][\s]|$))/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
      
      // Handle ordered lists
      .replace(/^[\s]*?\d+\.[\s]+(.*?)(?=(?:^[\s]*?\d+\.[\s]|$))/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/s, '<ol>$1</ol>')
      
      // Handle headings (h1 - h3 only for chat)
      .replace(/^### (.*$)/gm, '<h3>$1</h3>')
      .replace(/^## (.*$)/gm, '<h2>$1</h2>')
      .replace(/^# (.*$)/gm, '<h1>$1</h1>')
      
      // Handle links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      
      // Handle paragraphs and line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');

  // Wrap in paragraph if not already wrapped
  if (!parsed.startsWith('<')) {
      parsed = `<p>${parsed}</p>`;
  }

  return parsed;
};

const isAllowedImageUrl = (url) => {
  try {
      const urlObj = new URL(url);
      
      // List of allowed domains (add more as needed)
      const allowedDomains = [
          'v3.fal.media',
          'i.imgur.com',
          'images.unsplash.com',
          'upload.wikimedia.org',
          'via.placeholder.com',
          'google.com',
          // Add more trusted domains here
      ];
      
      // Check if URL has http/https protocol
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
          return false;
      }
      
      // Check if domain is in allowed list
      return allowedDomains.some(domain => 
          urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
      );
  } catch (e) {
      // Invalid URL format
      return false;
  }
};

  

const ChatWidget = ({ workflowId, config, settings }) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(config.design.position === 'inline');
    const [sessionId, setSessionId] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [messageQueue, setMessageQueue] = useState([]);
    const [showQuickResponses, setShowQuickResponses] = useState(true);
    const [dynamicInitialMessageLoaded, setDynamicInitialMessageLoaded] = useState(false);
    const [isLoadingInitialMessage, setIsLoadingInitialMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const quickResponsesRef = useRef(null);
    const [quickResponsesOverflow, setQuickResponsesOverflow] = useState({
    hasOverflow: false,
    atStart: true,
    atEnd: false
    });
    const [soundEnabled, setSoundEnabled] = useState(
        localStorage.getItem(`wp_ai_chat_sound_${workflowId}`) !== 'false'
    );
    const [isFirstMessage, setIsFirstMessage] = useState(true);
    const [hasPendingAction, setHasPendingAction] = useState(false);
    const actionPollRetryCountRef = useRef(0);
    
    // Refs
    const messagesEndRef = useRef(null);
    const eventSourceRef = useRef(null);

    const actionPollTimerRef = useRef(null);
    const audioRef = useRef({
        send: new Audio(`${settings.assetsUrl}/sounds/send.mp3`),
        receive: new Audio(`${settings.assetsUrl}/sounds/receive.mp3`)
    });
    const rateLimiter = useRef(new RateLimiter(
        config.behavior.rateLimit.maxMessages,
        config.behavior.rateLimit.timeWindow
    ));
    const [currentMessage, setCurrentMessage] = useState({
        message: '',
        timestamp: null
    });

    const [currentStreamedContent, setCurrentStreamedContent] = useState('');
    const [isStreaming, setIsStreaming] = useState(false);
    const streamSourceRef = useRef(null);
    const [shouldFetchDynamicMessage, setShouldFetchDynamicMessage] = useState(false);

    const shouldShowQuickResponses = 
    config.design.quickResponses && 
    Array.isArray(config.design.quickResponses) && 
    config.design.quickResponses.length > 0 &&
    !isTyping && 
    !hasPendingAction;



    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            // For inline mode, scroll the chat-messages container
            if (config.design.position === 'inline') {
                const chatMessagesContainer = messagesEndRef.current.closest('.chat-messages');
                if (chatMessagesContainer) {
                    chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight;
                }
            } else {
                // For popup mode, use scrollIntoView
                messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const checkQuickResponsesOverflow = useCallback(() => {
        if (!quickResponsesRef.current) return;
        
        const container = quickResponsesRef.current;
        
        // More reliable overflow detection with a small buffer
        const hasOverflow = container.scrollWidth > (container.clientWidth + 2); // 2px buffer for rounding errors
        const atStart = container.scrollLeft <= 5;
        const atEnd = (container.scrollWidth - container.scrollLeft - container.clientWidth) <= 5;
        
        
        setQuickResponsesOverflow({ hasOverflow, atStart, atEnd });
    }, []);
      
    // Handle scroll on quick responses container
    const handleQuickResponsesScroll = useCallback(() => {
        if (!quickResponsesRef.current) return;
        
        const container = quickResponsesRef.current;
        const atStart = container.scrollLeft <= 10;
        const atEnd = container.scrollWidth - container.scrollLeft - container.clientWidth <= 10;
        
        setQuickResponsesOverflow(prev => ({
          ...prev,
          atStart,
          atEnd
        }));
    }, []);

    const scrollQuickResponses = useCallback((direction) => {
        if (!quickResponsesRef.current) return;
        
        const container = quickResponsesRef.current;
        const scrollAmount = container.clientWidth * 0.8;
        
        if (direction === 'left') {
          container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
        } else {
          container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    }, []);

    const debugMode = false; // Set to true to enable verbose logging

    function debugLog(...args) {
    if (debugMode) {
        console.log(`[ChatWidget ${workflowId}]`, ...args);
    }
    }

    useEffect(() => {
      // Only set to true immediately for inline widgets
      if (config.design.position === 'inline') {
        setShouldFetchDynamicMessage(true);
      }
      // For non-inline widgets, leave it as false so it only happens on click
    }, []); 

    useEffect(() => {
        // Initial check
        const initialTimer = setTimeout(checkQuickResponsesOverflow, 100);
        
        // Check on resize
        window.addEventListener('resize', checkQuickResponsesOverflow);
        
        return () => {
          clearTimeout(initialTimer);
          window.removeEventListener('resize', checkQuickResponsesOverflow);
        };
    }, [checkQuickResponsesOverflow]);

    useEffect(() => {
        // Check overflow when quick responses change
        if (config.design.quickResponses?.length > 0) {
          const timer = setTimeout(checkQuickResponsesOverflow, 100);
          return () => clearTimeout(timer);
        }
    }, [config.design.quickResponses, checkQuickResponsesOverflow]);

    useEffect(() => {
        // When there's a new message from the assistant, re-enable quick responses
        if (messages.length > 0 && messages[messages.length - 1].role === 'assistant') {
          setShowQuickResponses(true);
        }
    }, [messages]);

    useEffect(() => {
      // Track changes to the persistence setting
      if (!config.behavior.persistHistory) {
          // When persistence is turned off, immediately clear localStorage
          localStorage.removeItem(`wp_ai_chat_session_${workflowId}`);
          debugLog("Persistence turned off - cleared saved session");
      }
    }, [config.behavior.persistHistory, workflowId]);
  
    const checkForPageReload = useCallback(() => {
        const pageLoadFlag = sessionStorage.getItem('wp_ai_chat_page_loaded');
        
        if (!pageLoadFlag) {
            // This is a fresh page load (not just a re-render)
            sessionStorage.setItem('wp_ai_chat_page_loaded', 'true');
            
            // If persistence is disabled, we should start with a fresh session
            if (!config.behavior.persistHistory) {
                // Clear any stored session for this workflow
                localStorage.removeItem(`wp_ai_chat_session_${workflowId}`);
                // We'll let the regular flow create a new session when needed
                debugLog("Page reload detected with persistence off - ensuring fresh session");
            }
        }
    }, [config.behavior.persistHistory, workflowId]);
    
    // Call this function on component mount
    useEffect(() => {
        checkForPageReload();
    }, [checkForPageReload]);
      

    useEffect(() => {
      // First check if persistence is disabled - if so, clear any stored session
      if (!config.behavior.persistHistory) {
          // Clear the stored session when persistence is disabled
          localStorage.removeItem(`wp_ai_chat_session_${workflowId}`);
          
          // If we already have a sessionId but persistence is turned off,
          // we should force a new session on the next reload
          if (sessionId) {
              // We don't reset the current session during active use,
              // but we make sure it won't be reused on next page load
              debugLog("Persistence disabled - session will not be saved");
          }
      }
      
      // Load chat history if we have a session
      if (sessionId) {
          fetchChatHistory();
      } else {
          // Only check localStorage if persistence is enabled
          if (config.behavior.persistHistory) {
              const storedSessionId = localStorage.getItem(`wp_ai_chat_session_${workflowId}`);
              if (storedSessionId) {
                  debugLog("Restoring saved session", storedSessionId);
                  setSessionId(storedSessionId);
              }
          } else {
              // If we don't have a session and persistence is off, do nothing
              // A new session will be created when the user sends their first message
              debugLog("No session yet and persistence disabled");
          }
      }
  
      return () => {
          if (eventSourceRef.current) {
              eventSourceRef.current.close();
          }
          if (actionPollTimerRef.current) {
              clearTimeout(actionPollTimerRef.current);
          }
      };
  }, [sessionId, workflowId, config.behavior.persistHistory]); 

  useEffect(() => {
    // For static initial message
    if (isFirstMessage && (!config.behavior.initialMessageType || config.behavior.initialMessageType === 'static') && config.behavior.initialMessage) {
      setMessages([{
        role: 'assistant',
        content: config.behavior.initialMessage
      }]);
      setIsFirstMessage(false);
    } 
    // For dynamic initial message - only fetch if shouldFetchDynamicMessage is true
    else if (isFirstMessage && config.behavior.initialMessageType === 'dynamic' && !dynamicInitialMessageLoaded && shouldFetchDynamicMessage) {
      if (!isLoadingInitialMessage) {
        fetchDynamicInitialMessage();
      }
    }
  }, [config.behavior.initialMessage, config.behavior.initialMessageType, isFirstMessage, dynamicInitialMessageLoaded, shouldFetchDynamicMessage, isLoadingInitialMessage]);
    

    // Check for action results if we have a pending action
    useEffect(() => {
        if (hasPendingAction && sessionId) {

          // Clear any existing timer first
          if (actionPollTimerRef.current) {
            clearTimeout(actionPollTimerRef.current);
          }
          // Set new timer
          actionPollTimerRef.current = setTimeout(checkForActionResults, 2000);
          
          // Cleanup function
          return () => {

            if (actionPollTimerRef.current) {
              clearTimeout(actionPollTimerRef.current);
            }
          };
        }
    }, [hasPendingAction, sessionId]);

    useEffect(() => {
        // Call scrollToBottom whenever messages change
        scrollToBottom();
        
        // Also handle resize events to adjust scrolling
        const handleResize = () => {
            setTimeout(scrollToBottom, 100); // Delay to ensure DOM updates
        };
        
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [messages]);

    useEffect(() => {

    }, [hasPendingAction]);

    const fetchChatHistory = async () => {
        try {
            const response = await fetch(
                `${settings.apiUrl}/chat-history?workflow_id=${workflowId}&session_id=${sessionId}`,
                {
                    headers: {
                        'X-WP-Nonce': settings.nonce
                    }
                }
            );
            const data = await response.json();
            if (data.success && data.history) {
                setMessages(data.history);
            }
        } catch (error) {

        }
    };

    const checkForActionResults = async () => {
        if (!sessionId) {
            debugLog("No session ID, stopping polling");
            setHasPendingAction(false);
            return;
        }
    
        // Add cache-busting parameter
        const url = `${settings.apiUrl}/chat-action-result?session_id=${sessionId}&_=${Date.now()}`;
        
        try {
            const response = await fetch(url, {
                headers: { 'X-WP-Nonce': settings.nonce }
            });
    
            if (!response.ok) throw new Error(`Server returned ${response.status}`);
    
            const data = await response.json();
            debugLog("Action result response", data);
    
            // First priority: check for completed result
            if (data.has_result && data.result?.content) {
                debugLog("Found final result", {
                    resultLength: data.result.content.length,
                    result: data.result
                });
                
                // Only filter out progress messages, not the initial response
                setMessages(prev => [...prev.filter(m => !m.isProgress), {
                    role: data.result.role || 'assistant',
                    content: data.result.content,
                    isActionResult: true,
                    timestamp: Date.now()
                }]);
    
                if (soundEnabled && config.behavior.soundEffects) {
                    playSound('receive');
                }
    
                // Clear states
                setHasPendingAction(false);
                actionPollRetryCountRef.current = 0;
                if (actionPollTimerRef.current) {
                    clearTimeout(actionPollTimerRef.current);
                }
                return;
            }
    
            const retryCount = actionPollRetryCountRef.current || 0;
            
            // Handle different states
            if (data.has_pending || data.status === 'processing') {
                // Still processing, use exponential backoff
                const nextDelay = Math.min(2000 * Math.pow(1.5, retryCount), 15000);
                debugLog("Still processing", { retryCount, nextDelay });
    
                actionPollRetryCountRef.current = retryCount + 1;
                actionPollTimerRef.current = setTimeout(checkForActionResults, nextDelay);
    
                // Show progress occasionally
                if (retryCount % 5 === 0) {
                    setMessages(prev => {
                        // Only remove existing progress messages, not initial response
                        const filtered = prev.filter(m => !m.isProgress);
                        return [...filtered, {
                            role: 'assistant',
                            content: 'Still processing your request, this might take a few minutes...',
                            isProgress: true
                        }];
                    });
                }
            } else if (data.status === 'completed') {
                // Completed but no result yet - use fixed interval
                debugLog("Waiting for result after completion", { retryCount });
            
                // Use a shorter fixed interval for polling after completion
                const nextDelay = 800; // 0.8 second fixed interval
                actionPollTimerRef.current = setTimeout(checkForActionResults, nextDelay);
                actionPollRetryCountRef.current = retryCount + 1;
            
                // Update message less frequently
                if (retryCount % 3 === 0) {
                    setMessages(prev => {
                        const filtered = prev.filter(m => !m.isProgress);
                        return [...filtered, {
                            role: 'assistant',
                            content: 'Processing completed, preparing results...',
                            isProgress: true
                        }];
                    });
                }
            
                // Stop after a reasonable amount of time
                if (retryCount > 120) {
                    debugLog("Giving up after long wait");
                    setHasPendingAction(false);
                    setErrorMessage('Unable to retrieve results. Please try again.');
                    return;
                }
            }
    
        } catch (error) {
            debugLog("Error checking action results", error);
            
            const retryCount = actionPollRetryCountRef.current || 0;
            if (retryCount < 30) {
                const nextDelay = Math.min(2000 * Math.pow(1.5, retryCount), 15000);
                actionPollRetryCountRef.current = retryCount + 1;
                actionPollTimerRef.current = setTimeout(checkForActionResults, nextDelay);
            } else {
                setHasPendingAction(false);
                setErrorMessage('Failed to retrieve results after multiple attempts');
            }
        }
    };

    // Add handler for header click
    const handleHeaderClick = (e) => {
        // If it's not an inline widget, allow closing via header click
        if (config.design.position !== 'inline') {
            e.stopPropagation();
            setIsOpen(false);
        }
    };

    const handleQuickResponseClick = (message) => {
        if (!message || !message.trim()) return;
        
        // We don't need to set inputValue since we're sending directly
        handleSendMessage(null, message);
        
    };


      
    // Update the renderQuickResponseButtons function
    const renderQuickResponseButtons = () => {
        if (!config.design.quickResponses || !Array.isArray(config.design.quickResponses) || config.design.quickResponses.length === 0) {
          return null;
        }
        
        // Only hide quick responses during an active request
        if (isTyping || hasPendingAction) {
          return null;
        }
        
        // Force immediate check without using a hook
        setTimeout(checkQuickResponsesOverflow, 50);
        
        const containerClasses = [
          'quick-responses-container',
          quickResponsesOverflow.hasOverflow ? 'has-overflow' : '',
          quickResponsesOverflow.atStart ? 'scroll-start' : '',
          quickResponsesOverflow.atEnd ? 'scroll-end' : ''
        ].filter(Boolean).join(' ');
        
        return (
          <div className={containerClasses}>
            {/* Left scroll button with chevron icon */}
            {(config.design.quickResponses.length > 1 && !quickResponsesOverflow.atStart) && (
              <button 
                className="quick-response-scroll-button quick-response-scroll-left"
                onClick={() => scrollQuickResponses('left')}
                aria-label="Scroll left"
              >
                <svg 
                  viewBox="0 0 24 24" 
                  width="12" 
                  height="12" 
                  stroke="currentColor" 
                  strokeWidth="3" 
                  fill="none" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
            )}
            
            <div 
              className="quick-response-buttons"
              ref={quickResponsesRef}
              onScroll={handleQuickResponsesScroll}
            >
              {config.design.quickResponses.map((item, index) => (
                <button
                  key={item.id || index}
                  className="quick-response-button"
                  onClick={() => handleQuickResponseClick(item.message)}
                  type="button"
                >
                  {item.text}
                </button>
              ))}
            </div>
            
            {/* Right scroll button with chevron icon */}
            {(config.design.quickResponses.length > 1 && !quickResponsesOverflow.atEnd) && (
              <button 
                className="quick-response-scroll-button quick-response-scroll-right"
                onClick={() => scrollQuickResponses('right')}
                aria-label="Scroll right"
              >
                <svg 
                  viewBox="0 0 24 24" 
                  width="12" 
                  height="12" 
                  stroke="currentColor" 
                  strokeWidth="3" 
                  fill="none" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            )}
          </div>
        );
    };


    const handleActionResponse = (data) => {
        setIsTyping(false); // Stop typing immediately
        
        // Add initial response right away, but without isProcessing flag
        const initialResponse = {
            role: 'assistant',
            content: data.message || "I'm processing your request..."
            // Removed isProcessing: true
        };
        
        setMessages(prev => [...prev, initialResponse]);
        setHasPendingAction(true);
        
        // Store action info
        const pendingKey = `wp_ai_workflows_pending_${data.session_id}`;
        localStorage.setItem(pendingKey, JSON.stringify({
            action_id: data.action_id,
            timestamp: Date.now()
        }));
        
    };

    const fetchDynamicInitialMessage = async () => {
      try {
        setIsLoadingInitialMessage(true);
        
        // Extract page context if enabled
        const pageContext = config.behavior.includePageContext ? config.page_context : null;
        
        // Prepare request parameters
        const requestParams = settings.isPreview 
          ? {
              message: "Generate an initial greeting message",
              session_id: sessionId,
              preview: true,
              is_initial_message: true,
              page_context: pageContext,
              preview_config: {
                model: config.model,
                system_prompt: config.systemPrompt,
                model_params: config.modelParams
              }
            }
          : {
              workflow_id: workflowId,
              message: "Generate an initial greeting message",
              session_id: sessionId,
              is_initial_message: true,
              page_context: pageContext
            };
        
        debugLog("Fetching dynamic initial message");
        
        // Reuse the existing chat endpoint
        const response = await fetch(`${settings.apiUrl}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': settings.nonce
          },
          body: JSON.stringify(requestParams)
        });
    
        if (!response.ok) throw new Error('Network response was not ok');
        
        const data = await response.json();
        
        // Store session ID if provided
        if (data.session_id) {
            setSessionId(data.session_id);
            // Only save to localStorage if persistence is enabled
            if (config.behavior.persistHistory) {
                localStorage.setItem(`wp_ai_chat_session_${workflowId}`, data.session_id);
                debugLog("Saved session to localStorage", data.session_id);
            }
        }
        
        // Add the initial message to the chat
        if (data.message) {
          setMessages([{
            role: 'assistant',
            content: data.message
          }]);
    
          if (soundEnabled && config.behavior.soundEffects) {
            playSound('receive');
          }
        } else {
          // Fallback to a generic message
          setMessages([{
            role: 'assistant',
            content: 'Hello! How can I help you today?'
          }]);
        }
        
        setIsFirstMessage(false);
      } catch (error) {
        debugLog("Error fetching dynamic initial message", error);
        
        // Fallback to a generic message
        setMessages([{
          role: 'assistant',
          content: 'Hello! How can I help you today?'
        }]);
        
        setIsFirstMessage(false);
      } finally {
        setDynamicInitialMessageLoaded(true);
        setIsLoadingInitialMessage(false);
      }
    };
    

    const handleSendMessage = async (e, messageText = null) => {
      try {
        if (e) e.preventDefault();
        const message = messageText || inputValue;
        if (!message.trim()) return;
    
        debugLog("Sending message", message);
    
        // Rate limit check
        if (!rateLimiter.current.canSendMessage()) {
          const waitTime = rateLimiter.current.getTimeUntilNextAllowed();
          setErrorMessage(`Please wait ${Math.ceil(waitTime/1000)} seconds before sending another message`);
          return;
        }
    
        const cleanMessage = sanitizeInput(message);
        if (!cleanMessage) {
          setErrorMessage('Invalid message content');
          return;
        }
    
        const newMessageData = {
          message: cleanMessage,
          timestamp: Date.now()
        };
        setCurrentMessage(newMessageData);
        rateLimiter.current.recordMessage();
    
        if (!messageText) {
          setInputValue('');
        }
        setErrorMessage('');
        
        // Add user message
        const userMessage = { role: 'user', content: newMessageData.message };
        setMessages(prev => [...prev, userMessage]);
    
        if (soundEnabled && config.behavior.soundEffects) {
          playSound('send');
        }
    
        // Check if streaming is enabled
        const useStreaming = config.behavior.streamResponses !== false;
    
        if (useStreaming) {
          // Handle streaming response
          await handleStreamingResponse(cleanMessage);
        } else {
          // Handle regular response (existing code)
          setIsTyping(true);
          handleRegularResponse(cleanMessage);
        }
      } catch (error) {
        debugLog("Error in handleSendMessage", error);
        setIsTyping(false);
        setIsStreaming(false);
        
        // Cleanup any streaming connection
        if (streamSourceRef.current) {
          streamSourceRef.current.close();
          streamSourceRef.current = null;
        }
    
        // Check if action was started despite error
        const pendingKey = `wp_ai_workflows_pending_${sessionId}`;
        const pendingAction = localStorage.getItem(pendingKey);
        
        if (pendingAction) {
          const { action_id } = JSON.parse(pendingAction);
          handleActionResponse({
            session_id: sessionId,
            action_id: action_id,
            message: "I'm processing your request. This may take a few minutes..."
          });
        } else {
          setErrorMessage('Failed to send message. Please try again.');
        }
      } finally {
        // Clean up
        setCurrentMessage({ message: '', timestamp: null });
      }
    };
    
    // Add these new helper functions for streaming
    
    const handleStreamingResponse = async (cleanMessage) => {
      // Clean up any existing stream
      if (streamSourceRef.current) {
        streamSourceRef.current.close();
        streamSourceRef.current = null;
      }
    
      // Indicate streaming is active
      setIsStreaming(true);
      
      // Create a unique ID for this streaming session
      const streamId = Date.now();
      const streamingElementId = `streaming-message-${streamId}`;
      
      // First add the message to the React state so it creates the DOM structure
      setMessages(prev => {
        return [...prev, { 
          role: 'assistant', 
          content: '',
          isStreaming: true,
          streamId,
          elementId: streamingElementId
        }];
      });
      
      // Wait a tiny bit for React to render the initial message container
      await new Promise(resolve => setTimeout(resolve, 50));
      
      try {
        // Setup request parameters (same as your current working code)
        const requestParams = settings.isPreview 
          ? {
              message: cleanMessage,
              session_id: sessionId, // Make sure sessionId is passed
              preview: true,
              page_context: config.page_context || null,
              preview_config: {
                model: config.model,
                system_prompt: config.systemPrompt,
                model_params: config.modelParams,
                actions: config.actions || []
              }
            }
          : {
              workflow_id: workflowId,
              message: cleanMessage,
              session_id: sessionId, // Make sure sessionId is passed
              page_context: config.page_context || null
            };
    
        // Debug log session state before request
        console.debug('Streaming request with session ID:', sessionId);
    
        const response = await fetch(`${settings.apiUrl}/stream-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': settings.nonce
          },
          body: JSON.stringify(requestParams)
        });
    
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
    
        // Get the DOM element for direct manipulation
        const messageElement = document.querySelector(`#${streamingElementId} .message-content`);
        if (!messageElement) {
          console.error("Could not find streaming message element in DOM");
          return;
        }
        
        // Stream processing variables
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let fullResponse = '';
        
        // Typewriter effect variables
        let displayedText = '';
        let isTyping = false;
        let typingSpeed = 2;
        let typingInterval = 20;
        
        // Typewriter animation function
        const typeNextChars = () => {
          if (displayedText.length < fullResponse.length) {
            const charsToType = Math.min(typingSpeed, fullResponse.length - displayedText.length);
            displayedText = fullResponse.substring(0, displayedText.length + charsToType);
            
            // Update the DOM with new text
            messageElement.innerHTML = sanitizeContent(parseMarkdown(displayedText));
            
            // Scroll to ensure visibility
            scrollToBottom();
            
            // Continue typing after a delay
            setTimeout(typeNextChars, typingInterval);
          } else {
            isTyping = false;
          }
        };
    
        // Process the stream
        const processStream = async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              
              if (done) {
                break;
              }
              
              // Decode the chunk and add to buffer
              const chunk = decoder.decode(value, { stream: true });
              buffer += chunk;
              
              // Process complete SSE messages from buffer
              while (true) {
                const endIndex = buffer.indexOf('\n\n');
                if (endIndex === -1) break;
                
                const line = buffer.slice(0, endIndex);
                buffer = buffer.slice(endIndex + 2);
                
                if (line.startsWith('data: ')) {
                  const data = line.slice(6);
                  
                  // End of stream signal
                  if (data === '[DONE]') {
                    // Wait for typing to finish
                    const waitForTyping = () => {
                      if (isTyping) {
                        setTimeout(waitForTyping, 100);
                      } else {
                        // Final message is complete - finish streaming
                        setIsStreaming(false);
                        
                        // Update React state with the final content
                        setMessages(prev => {
                          return prev.map(msg => {
                            if (msg.streamId === streamId) {
                              return { 
                                ...msg, 
                                isStreaming: false, 
                                content: fullResponse
                              };
                            }
                            return msg;
                          });
                        });
                        
                        if (soundEnabled && config.behavior.soundEffects) {
                          playSound('receive');
                        }
                      }
                    };
                    
                    waitForTyping();
                    return;
                  }
                  
                  try {
                    const parsed = JSON.parse(data);
                    
                    // Check if we received a session ID - IMPORTANT: Add this section
                    if (parsed.session_id && (!sessionId || sessionId !== parsed.session_id)) {
                      console.debug('Setting session ID from stream:', parsed.session_id);
                      setSessionId(parsed.session_id);
                      localStorage.setItem(`wp_ai_chat_session_${workflowId}`, parsed.session_id);
                    }
                    
                    // Handle content chunks
                    if (parsed.content !== undefined) {
                      // Add to the full response
                      fullResponse += parsed.content;
                      
                      // Start typing if not already typing
                      if (!isTyping) {
                        isTyping = true;
                        setTimeout(typeNextChars, typingInterval);
                      }
                    }
                  } catch (e) {
                    console.error('Error parsing JSON in SSE', e);
                  }
                }
              }
            }
          } catch (err) {
            console.error('Stream reading error:', err);
            setIsStreaming(false);
          }
        };
    
        // Start processing the stream
        processStream();
        
        // Store for cleanup
        streamSourceRef.current = { 
          close: () => {
            try {
              reader.cancel();
            } catch (e) {
              console.error('Error cancelling stream:', e);
            }
          }
        };
      } catch (error) {
        console.error('Error setting up stream:', error);
        setIsStreaming(false);
        setErrorMessage('Failed to connect to streaming service');
      }
    };


    const renderMessages = () => {
      return messages.map((message, index) => {
        const messageClasses = `chat-message ${message.role} ${message.isStreaming ? 'streaming' : ''}`;
        return (
          <div 
            key={`${index}-${message.streamId || ''}`} 
            className={messageClasses}
            id={message.elementId || `message-${index}`} // Add this ID for direct DOM manipulation
          >
            <div 
              className="message-content markdown-content"
              dangerouslySetInnerHTML={{ 
                __html: sanitizeContent(parseMarkdown(message.content || '')) 
              }} 
            />
          </div>
        );
      });
    };
    
    // Add this for regular (non-streaming) responses
    const handleRegularResponse = async (cleanMessage) => {
      setIsTyping(true);
    
      const response = await fetch(`${settings.apiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': settings.nonce
        },
        body: JSON.stringify(
          settings.isPreview 
          ? {
              workflow_id: null,
              message: cleanMessage,
              session_id: sessionId,
              preview: true,
              page_context: config.page_context || null,
              preview_config: {
                model: config.model,
                system_prompt: config.systemPrompt,
                model_params: config.modelParams,
                actions: config.actions || []
              }
            }
          : {
              workflow_id: workflowId,
              message: cleanMessage,
              session_id: sessionId,
              page_context: config.page_context || null
            }
        )
      });
    
      if (!response.ok) throw new Error('Network response was not ok');
    
      const data = await response.json();
      debugLog("Received API response", data);
    
      if (data.citations) {
        debugLog("Received citations", data.citations);
      }
    
      // Store session ID if provided
      if (data.session_id) {
        setSessionId(data.session_id);
        // Only save to localStorage if persistence is enabled
        if (config.behavior.persistHistory) {
            localStorage.setItem(`wp_ai_chat_session_${workflowId}`, data.session_id);
            debugLog("Saved session to localStorage", data.session_id);
        }
    }
    
      if (data.type === 'action') {
        handleActionResponse(data);
      } else {
        // Handle regular message
        setIsTyping(false);
        
        // Process citations if they exist and the setting is enabled
        let messageContent = data.message;
        
        // Process citations if we have them and they're enabled
        if (data.citations && 
            Array.isArray(data.citations) && 
            data.citations.length > 0 && 
            config.behavior.showCitations) {
          messageContent = processCitations(data.message, data.citations);
        }
        
        const assistantMessage = { 
          role: 'assistant', 
          content: messageContent,
          citations: data.citations || []
        };
        
        setMessages(prev => [...prev, assistantMessage]);
        
        if (soundEnabled && config.behavior.soundEffects) {
          playSound('receive');
        }
      }
    };
    
    // Make sure to update the cleanup in useEffect to close any active streams
    useEffect(() => {
      return () => {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }
        if (actionPollTimerRef.current) {
          clearTimeout(actionPollTimerRef.current);
        }
        if (streamSourceRef.current) {
          streamSourceRef.current.close();
        }
      };
    }, []);
    
    const renderError = () => {
        if (!errorMessage) return null;
        return (
            <div className="error-message" role="alert">
                {errorMessage}
            </div>
        );
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
    };

    const processCitations = (message, citations) => {
        if (!citations || !Array.isArray(citations) || citations.length === 0) {
          return message;
        }
      
        let processedMessage = message;
        
        // Separate file citations and URL citations
        const fileCitations = citations.filter(citation => citation.type === 'file_citation');
        const urlCitations = citations.filter(citation => citation.type === 'url_citation');
        
        if (fileCitations.length > 0 || urlCitations.length > 0) {
          // Add a divider
          processedMessage += "\n\n---\n\n**Sources:**\n";
          
          // Process URL citations
          if (urlCitations.length > 0) {
            // Get unique URLs
            const uniqueUrls = [];
            urlCitations.forEach(citation => {
              if (!uniqueUrls.some(c => c.url === citation.url)) {
                uniqueUrls.push(citation);
              }
            });
            
            uniqueUrls.forEach((citation, index) => {
              const url = citation.url;
              const title = citation.title || url;
              processedMessage += `${index + 1}. [${title}](${url})\n`;
            });
          }
          
          // Process file citations
          if (fileCitations.length > 0) {
            // Get unique files
            const uniqueFiles = [];
            fileCitations.forEach(citation => {
              if (!uniqueFiles.some(c => c.file_id === citation.file_id)) {
                uniqueFiles.push(citation);
              }
            });
            
            // Add file citations after URL citations
            const startIndex = urlCitations.length;
            uniqueFiles.forEach((citation, index) => {
              processedMessage += `${startIndex + index + 1}. File: ${citation.filename}\n`;
            });
          }
        }
        
        return processedMessage;
    };

    const getBotIcon = () => {
        switch (config.design.botIcon) {
          case 'robot':
            return <RobotOutlined style={{ fontSize: '20px' }} />;
          case 'assistant':
            return <CustomerServiceOutlined style={{ fontSize: '20px' }} />;
          case 'brain':
            return <BulbOutlined style={{ fontSize: '20px' }} />;
          case 'chat':
            return <MessageOutlined style={{ fontSize: '20px' }} />;
          default:
            return <RobotOutlined style={{ fontSize: '20px' }} />;
        }
    };

    const playSound = (type) => {
        try {
            if (soundEnabled && audioRef.current[type]) {
                audioRef.current[type].play().catch(() => {
                    // Silently fail - sound playback isn't critical
                    console.log('Sound playback failed');
                });
            }
        } catch (error) {
            // Don't show error to user - sound isn't critical
            console.log('Sound playback error');
        }
    };

    const toggleSound = () => {
        setSoundEnabled(!soundEnabled);
        // Store sound preference
        localStorage.setItem(`wp_ai_chat_sound_${workflowId}`, !soundEnabled);
    };

    // Get dynamic styles based on config
    const getStyles = () => ({
        container: {
            width: config.design.position === 'inline' ? '100%' : `${config.design.dimensions.width}px`,
            height: config.design.position === 'inline' ? 'auto' : `${config.design.dimensions.height}px`,
            fontFamily: config.design.font.family,
            fontSize: config.design.font.size,
            '--chat-border-radius': `${config.design.dimensions.borderRadius}px`,
            '--chat-messages-height': `${config.design.position === 'inline' ? 
                Math.min(400, config.design.dimensions.height - 140) : 
                'auto'}px`,
            ...(config.design.position === 'inline' ? {
                position: 'relative',
                width: '100%',
                height: 'auto',
                minHeight: '500px',
                margin: '20px 0',
                boxShadow: 'none'
            } : {}),
            ...getThemeStyles()
        },
        header: {
            fontSize: config.design.font.headerSize,
            borderTopLeftRadius: `${config.design.dimensions.borderRadius}px`,
            borderTopRightRadius: `${config.design.dimensions.borderRadius}px`,
        },
        messages: {
            fontSize: config.design.font.size,
            maxHeight: config.design.position === 'inline' ? 
                `${Math.min(400, config.design.dimensions.height - 140)}px` : 
                'auto'
        }
    });

    const getThemeStyles = () => {
        if (config.design.theme === 'custom') {
            return {
                '--chat-primary': config.design.colors.primary,
                '--chat-bg': config.design.colors.background,
                '--chat-text': config.design.colors.text,
                '--chat-secondary': config.design.colors.secondary,
            };
        }
        return {};
    };

    if (!isOpen && config.design.position !== 'inline') {
      return (
          <button 
              className={`chat-launcher ${config.design.position}`}
              onClick={() => {
                  setIsOpen(true);
                  // Set flag to fetch dynamic message if needed
                  if (isFirstMessage && config.behavior.initialMessageType === 'dynamic' && !dynamicInitialMessageLoaded) {
                      setShouldFetchDynamicMessage(true);
                  }
              }}
              aria-label="Open chat"
          >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
          </button>
      );
  }

    return (
        <div className={`wp-ai-workflows-chat-widget ${config.design.theme} ${config.design.position}`}
                style={getStyles().container}>
                <div className="chat-header" style={getStyles().header} onClick={handleHeaderClick}>
                    <div className="header-content">
                        {getBotIcon()}
                        <span className="bot-name">{config.design.botName}</span>
                    </div>
                    <div className="header-actions">
                        <button 
                            className="sound-toggle"
                            onClick={(e) => {
                                e.stopPropagation(); // Prevent header click
                                toggleSound();
                            }}
                            aria-label={soundEnabled ? 'Mute sound' : 'Unmute sound'}
                        >
                            {soundEnabled ? <SoundOutlined /> : <MutedOutlined />}
                        </button>
                        {config.design.position !== 'inline' && (
                            <button 
                                className="close-button"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent header click
                                    setIsOpen(false);
                                }}
                                aria-label="Close chat"
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>

                <div className="chat-messages">
                  {renderMessages()}
                  {renderError()}
                  {(isTyping || hasPendingAction || isLoadingInitialMessage) && !isStreaming && (
                    <div className="typing-indicator">
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                      <span className="typing-dot"></span>
                    </div>
                  )}
                  {messages.length > 0 && <div ref={messagesEndRef} style={{ height: '1px' }} />}
                </div>

                <div className="quick-responses-area">
                    
                    {shouldShowQuickResponses && renderQuickResponseButtons()}
                    </div>

            <div className="chat-input">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={config.behavior.placeholderText}
                    disabled={isTyping || hasPendingAction}
                />
              <button 
                onClick={(e) => handleSendMessage(e)}
                disabled={isTyping || hasPendingAction || !inputValue.trim()}
                dangerouslySetInnerHTML={{ 
                  __html: config.design.sendButtonText || "Send" 
                }}
              >
              </button>
            </div>
            {config.design.showPoweredBy !== false && (
              <div className="powered-by">
                  <a 
                      href="https://bit.ly/3CEdP35"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Create your own AI chatbot"
                  >
                      Powered by AI Workflow Automation Plugin
                  </a>
              </div>
          )}
        </div>

    );
};

ChatWidget.propTypes = {
    workflowId: PropTypes.string.isRequired,
    config: PropTypes.object.isRequired,
    settings: PropTypes.shape({
        apiUrl: PropTypes.string.isRequired,
        nonce: PropTypes.string.isRequired,
        siteUrl: PropTypes.string.isRequired,
        assetsUrl: PropTypes.string.isRequired
    }).isRequired
};

export default ChatWidget;